import {HttpErrorResponse} from '@angular/common/http';
import {Injectable, Optional} from '@angular/core';
import {BehaviorSubject, TimeoutError} from 'rxjs';
import {ApiCompatibilityFailureState, ApiCompatibilityService, ApiCompatibilityState} from '../api/api-compatibility.service';
import {markSystemApiErrorPresented, wasSystemApiErrorPresented} from './local-api-error';

export interface ApiErrorDialogOptions {
  messageKey?: string;
  retry?: () => void;
  details?: string;
}

@Injectable({
  providedIn: 'root',
})
export class ServerErrorDialogService {
  private readonly stateSubject = new BehaviorSubject<boolean>(false);

  readonly isOpen$ = this.stateSubject.asObservable();
  titleKey = 'auth.common.serverErrorTitle';
  messageKey = 'auth.common.serverErrorMessage';
  details = '';
  private retryAction?: () => void;
  private systemActive = false;
  private compatibilityFailure?: ApiCompatibilityFailureState;
  private compatibilityAlert = false;
  private compatibilityScoped = false;

  constructor(@Optional() private readonly compatibility?: ApiCompatibilityService) {}

  get canRetry(): boolean { return !!this.retryAction; }

  get isConfirmedIncompatibility(): boolean {
    return this.stateSubject.value && this.compatibilityAlert && this.compatibilityFailure === 'incompatible';
  }

  open(options?: ApiErrorDialogOptions): void {
    if (this.compatibility?.recoveryVisible()) return;
    if (this.hasCompatibilityFailure()) return;
    if (this.systemActive && this.stateSubject.value) return;
    if (this.stateSubject.value && (!options || options.details !== undefined)) {
      return;
    }

    this.titleKey = 'auth.common.serverErrorTitle';
    this.messageKey = options?.messageKey ?? 'auth.common.serverErrorMessage';
    this.retryAction = options?.retry;
    this.details = options?.details ?? (this.stateSubject.value ? this.details : '');
    this.stateSubject.next(true);
  }

  openSystem(error: HttpErrorResponse | TimeoutError, details: string): void {
    markSystemApiErrorPresented(error);
    if (error instanceof TimeoutError || error.status === 0) {
      this.close();
      this.compatibility?.reportUnavailable();
      return;
    }
    if (this.compatibility?.recoveryVisible()) return;
    if (this.hasCompatibilityFailure()) return;
    if (this.systemActive && this.stateSubject.value) return;
    this.systemActive = true;
    this.titleKey = 'auth.common.serverErrorTitle';
    this.messageKey = 'auth.common.serverErrorMessage';
    this.retryAction = undefined;
    this.details = details;
    this.stateSubject.next(true);
  }

  /** One host presents failures only; pending probes have no dialog. */
  syncCompatibilityFailure(state?: ApiCompatibilityState): void {
    this.compatibilityScoped = !!state;
    if (this.compatibility?.recoveryVisible()) {
      this.compatibilityFailure = undefined;
      if (this.stateSubject.value) this.close();
      return;
    }
    const failure = state === 'checking' || state === 'compatible' ? undefined : state;
    if (this.compatibilityFailure === failure) return;
    this.compatibilityFailure = failure;
    if (!failure) {
      if (this.compatibilityAlert) this.close();
      return;
    }
    this.compatibilityAlert = true;
    this.systemActive = true;
    const unexpected = failure === 'unavailable' && !this.compatibility?.serverUnavailable();
    this.titleKey = unexpected ? 'auth.common.serverErrorTitle' : `apiCompatibility.${failure}.title`;
    this.messageKey = unexpected ? 'auth.common.serverErrorMessage' : `apiCompatibility.${failure}.message`;
    this.details = this.compatibility?.failureDetails() ?? '';
    this.retryAction = () => {
      this.syncCompatibilityFailure('checking');
      void this.compatibility?.recheck().then(result => {
        if (this.compatibilityScoped) this.syncCompatibilityFailure(result);
      });
    };
    this.stateSubject.next(true);
  }

  private hasCompatibilityFailure(): boolean {
    // HTTP errors can arrive before Angular applies the probe's new state to the host.
    if (this.compatibilityScoped && this.compatibility) this.syncCompatibilityFailure(this.compatibility.state());
    return !!this.compatibilityFailure;
  }

  wasSystemErrorPresented(error: unknown): boolean {
    return wasSystemApiErrorPresented(error);
  }

  close(): void {
    this.compatibilityAlert = false;
    this.systemActive = false;
    this.retryAction = undefined;
    this.details = '';
    this.stateSubject.next(false);
  }

  retry(): void {
    const action = this.retryAction;
    this.close();
    action?.();
  }
}
